import type {ComponentEnhancer} from 'recompose'
import {branch, withHandlers} from 'recompose'

import Link from '@jetbrains/ring-ui/components/link/clickableLink'
import classNames from 'classnames'
import compose from 'lodash/flowRight'
import type * as React from 'react'
import {graphql, useFragment} from 'react-relay'
import {useLocation} from 'react-router-dom'

import {useRunningBuildUpdaterRelay} from '../../../../hooks/relay/useRunningBuildUpdaterRelay'
import {useBuildHrefWithSaveParams} from '../../../../hooks/routes'
import {getDuration, getTimeInQueue} from '../../../../utils/buildStats'
import {useBuildNumberText} from '../../BuildNumber/BuildNumber.utils'
import RouterLinkWrapper from '../../RouterLinkWrapper/RouterLinkWrapper'
import {withVisibility} from '../../Visible/Visible.hocs'

import type {Props, InnerProps, HandlerProps} from './BuildStatsBar.types'

import styles from './BuildStatsBar.css'

export const SAVED_BUILD_QUERY_PARAMS = ['buildTab', 'filter', 'filterText']
export const MAX_HEIGHT = 30

const fragment = graphql`
  fragment BuildStatsBarFragment on Build {
    rawId
    startDate
    queuedDate
    finishDate
    runningInfo {
      elapsedSeconds
    }
    status
    state
    ...useRunningBuildUpdaterRelayFragment
  }
`

function BuildStatsBar({
  buildKey,
  withSaveQueryParams,
  maxDuration,
  isCurrent,
  isHovered,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  isVisible,
  maxTimeInQueue,
}: InnerProps) {
  const build = useFragment(fragment, buildKey)
  const duration = getDuration(build ?? {})
  const timeInQueue = getTimeInQueue(build ?? {})
  const {status, state, rawId} = build ?? {}
  const location = useLocation()
  const buildHref = useBuildHrefWithSaveParams(
    location,
    rawId,
    withSaveQueryParams === true ? SAVED_BUILD_QUERY_PARAMS : [],
  )
  const durationHeight = maxDuration > 0 ? (MAX_HEIGHT * duration) / maxDuration : MAX_HEIGHT
  useRunningBuildUpdaterRelay(build)
  return (
    <RouterLinkWrapper>
      <Link
        href={buildHref}
        className={classNames(styles.buildLink, {
          [styles.current]: isCurrent,
          [styles.hovered]: isHovered,
        })}
        onFocus={onFocus}
        onBlur={onBlur}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label={`Build ${useBuildNumberText(rawId)}`}
      >
        <div className={styles.column}>
          <div
            style={{
              height: durationHeight,
              marginTop: MAX_HEIGHT - durationHeight,
            }}
            className={classNames(styles.half, {
              [styles.animate]: isVisible,
              [styles.running]: state === 'running',
              [styles.success]: status === 'SUCCESS',
              [styles.canceled]: status === 'UNKNOWN',
              [styles.failed]: status !== 'SUCCESS' && status !== 'UNKNOWN',
            })}
          />
          <div
            style={{
              height: maxTimeInQueue > 0 ? (MAX_HEIGHT * timeInQueue) / maxTimeInQueue : MAX_HEIGHT,
            }}
            className={classNames(styles.half, styles.timeInQueue)}
          />
        </div>
      </Link>
    </RouterLinkWrapper>
  )
}

export default compose(
  withHandlers({
    onMouseEnter:
      ({hoverBuild, id}: Props) =>
      (e: React.MouseEvent) =>
        typeof hoverBuild === 'function' && hoverBuild(id, e),
    onMouseLeave:
      ({hoverBuild}: Props) =>
      (e: React.MouseEvent) =>
        typeof hoverBuild === 'function' && hoverBuild(null, e),
    onFocus:
      ({selectBuild, id}: Props) =>
      () =>
        typeof selectBuild === 'function' && selectBuild(id),
    onBlur:
      ({selectBuild}: Props) =>
      () =>
        typeof selectBuild === 'function' && selectBuild(null),
  }),
  branch<InnerProps>(
    props => props.state === 'running',
    withVisibility(false) as ComponentEnhancer<InnerProps, Props & HandlerProps>,
  ),
)(BuildStatsBar)
